
import React, { useEffect, useState } from 'react';
import { Card, List, Typography, Spin, Alert, Divider } from 'antd';
import { User } from '../../types';
import api from '../../services/api';
import './ActivitySection.css';

const { Title, Text } = Typography;

interface ActivityLog {
  activityType: string;
  timestamp: string;
  ip: string | null;
  device: string | null;
  location: string | null;
  browser: string | null;
}

/**
 * What each recorded activity is called on screen. Anything not listed falls
 * back to its stored name with the underscores taken out, so an activity
 * added by a newer backend reads as something rather than as nothing.
 */
const ACTIVITY_LABELS: Record<string, string> = {
  login: 'Signed in',
  create_user: 'Account created',
  activate_account: 'Account activated',
  change_password: 'Password changed',
  update_profile: 'Profile updated',
  delete_user: 'Account deleted',
};

const activityLabel = (type: string): string =>
  ACTIVITY_LABELS[type] ??
  (type ? type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : 'Activity');

/**
 * The where of an activity, as one line.
 *
 * Every one of these can be absent: entries recorded before sign-ins were
 * logged have none of them, and location needs a geolocation lookup this app
 * does not do. Listing them as empty labels is what made this page look
 * broken, so only what is known is shown.
 */
const activityContext = (log: ActivityLog): string =>
  [log.ip, log.device, log.browser, log.location].filter(Boolean).join(' · ');

interface ActivitySectionProps {
  user: User;
}

const ActivitySection: React.FC<ActivitySectionProps> = ({ user }) => {
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivityLogs = async () => {
      try {
        const response = await api.get('/user/me/activity');
        // The backend returns these newest first and capped; the page shows
        // the most recent handful.
        setActivityLogs((response.data ?? []).slice(0, 6));
      } catch (err: any) {
        console.error(err);
        setError(err.response?.data?.message || 'An error occurred while fetching activity logs.');
      } finally {
        setLoading(false);
      }
    };

    fetchActivityLogs();
  }, [user.uid]);

  return (
    <Card
      title="Account Activity"
      bordered={false}
      className="activity-card"
      bodyStyle={{ padding: '2rem' }}
    >
      {loading ? (
        <Spin tip="Loading activity logs..." />
      ) : error ? (
        <Alert message="Error" description={error} type="error" showIcon />
      ) : activityLogs.length === 0 ? (
        <Text>No activity logs available.</Text>
      ) : (
        <List
          itemLayout="vertical"
          dataSource={activityLogs}
          renderItem={(log) => (
            <List.Item className="activity-item">
              <List.Item.Meta
                title={<Text strong>{activityLabel(log.activityType)}</Text>}
                description={
                  <div>
                    <Text type="secondary">{new Date(log.timestamp).toLocaleString()}</Text>
                    {activityContext(log) ? (
                      <>
                        <br />
                        <Text type="secondary">{activityContext(log)}</Text>
                      </>
                    ) : null}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

export default ActivitySection;
